import { Instagram, Facebook, Youtube, Twitter, MapPin, Phone, Mail } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_62fe82b4-8a19-46f2-ac06-6bb7b1c3a21c/artifacts/jzp1y8sq_Aham-Yoga-Institute-logo.png";

const QUICK_LINKS = [
    { label: "Home", hash: "#home" },
    { label: "Courses", hash: "#courses" },
    { label: "Products", hash: "#products" },
    { label: "About Us", to: "/about" },
    { label: "Contact", hash: "#contact" },
];

const SOCIALS = [
    {
        label: "Instagram",
        href: "https://www.instagram.com/ahamyoginstitute/",
        Icon: Instagram,
    },
    {
        label: "Facebook",
        href: "https://www.facebook.com/ahamyoginstitute",
        Icon: Facebook,
    },
    { label: "YouTube", href: "#", Icon: Youtube },
    { label: "Twitter", href: "#", Icon: Twitter },
];

export default function Footer() {
    const location = useLocation();
    const navigate = useNavigate();
    const goTo = (hash) => {
        if (location.pathname === "/") {
            const id = hash.replace("#", "");
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate(`/${hash}`);
        }
    };

    return (
        <footer className="bg-[#0A0A0A] text-gray-200" data-testid="footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid md:grid-cols-4 gap-10">
                    {/* Brand */}
                    <div className="space-y-5">
                        <Link to="/" className="flex items-center gap-3">
                            <span className="h-12 w-12 rounded-2xl overflow-hidden bg-white ring-1 ring-white/10 flex items-center justify-center p-1">
                                <img src={LOGO_URL} alt="Aham Yog" className="max-h-full max-w-full object-contain" />
                            </span>
                            <div className="leading-tight">
                                <div className="font-heading font-semibold text-lg text-white">
                                    Aham Yog
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                                    Institute
                                </div>
                            </div>
                        </Link>
                        <p className="text-sm text-gray-400 leading-relaxed">
                            A sanctuary for breath, movement and stillness. Authentic
                            yoga education and thoughtfully-made essentials, rooted in
                            tradition.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-heading font-semibold text-white text-base mb-5">
                            Quick Links
                        </h4>
                        <ul className="space-y-3">
                            {QUICK_LINKS.map((l) => (
                                <li key={l.label}>
                                    <button
                                        onClick={() => l.to ? navigate(l.to) : goTo(l.hash)}
                                        className="text-sm text-gray-400 hover:text-brand-300 transition-colors text-left"
                                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        {l.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Studio */}
                    <div>
                        <h4 className="font-heading font-semibold text-white text-base mb-5">
                            Studio
                        </h4>
                        <ul className="space-y-3 text-sm text-gray-400">
                            <li className="flex items-start gap-2.5">
                                <MapPin className="h-4 w-4 mt-0.5 text-brand-400 shrink-0" />
                                <span>
                                    Aham Yog Institute,
                                    <br />
                                    Mumbai, Maharashtra, India
                                </span>
                            </li>
                            <li className="flex items-center gap-2.5">
                                <Phone className="h-4 w-4 text-brand-400" />
                                <a href="tel:+910000000000" className="hover:text-brand-300">
                                    +91 00000 00000
                                </a>
                            </li>
                            <li className="flex items-center gap-2.5">
                                <Mail className="h-4 w-4 text-brand-400" />
                                <a href="mailto:hello@ahamyog.com" className="hover:text-brand-300">
                                    hello@ahamyog.com
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Social */}
                    <div>
                        <h4 className="font-heading font-semibold text-white text-base mb-5">
                            Follow Us
                        </h4>
                        <p className="text-sm text-gray-400 leading-relaxed">
                            Daily inspiration, practice tips and stories from the mat.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-3">
                            {SOCIALS.map(({ label, href, Icon }) => (
                                <a
                                    key={label}
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={label}
                                    className="h-10 w-10 rounded-full bg-white/5 hover:bg-brand-500 hover:text-white text-gray-300 flex items-center justify-center transition-colors border border-white/10"
                                    data-testid={`social-${label.toLowerCase()}`}
                                >
                                    <Icon className="h-4.5 w-4.5" strokeWidth={1.6} />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
                    <p className="text-xs text-gray-500">
                        &copy; {new Date().getFullYear()} Aham Yog Institute. All rights
                        reserved.
                    </p>
                    <p className="text-xs text-gray-500">
                        Made with breath, movement and a little love.
                    </p>
                </div>
            </div>
        </footer>
    );
}
